import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import authService from '../services/authService';
import { Copy } from 'lucide-react';

/**
 * ProductPlansManager
 * UI for managing plans (consumption/quota) associated with a product.
 * - List all plans for the selected product
 * - Create new plans (consumption/quota)
 * - Enable/disable (activate/deactivate) plans
 * - Edit and delete plans
 */
export default function ProductPlansManager({ selectedProduct, teamId }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showInactive, setShowInactive] = useState(true); // New state for toggling inactive plans visibility

  // Form state for creating a new plan
  const [newPlan, setNewPlan] = useState({
    name: '',
    type: 'consumption',
    quota: '',
    deliveryMode: 'cloud',
    active: true // Explicitly set new plans to active by default
  });

  // State for editing a plan
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    type: '',
    quota: '',
  });

  // Fetch plans for the selected product
  const fetchPlans = useCallback(async () => {
    if (!selectedProduct) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/plans?productId=${selectedProduct.id}&include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch plans');
      setPlans(data.plans || []);
    } catch (err) {
      setError(err.message);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const copyToClipboard = async (value) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
    }
  };

  // Debug function to check plan data
  useEffect(() => {
    if (plans.length > 0) {
      console.log('Current plans:', plans);
    }
  }, [plans]);

  // Handle form input changes for new plan
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPlan((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form input changes for edit plan
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Start editing a plan
  const handleStartEdit = (plan) => {
    setEditingPlan(plan);
    setEditForm({
      name: plan.name,
      type: plan.type,
      quota: plan.type === 'quota' ? String(plan.quota || '') : '',
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditForm({ name: '', type: '', quota: '' });
  };

  // Handle toggle show inactive plans
  const handleToggleShowInactive = () => {
    setShowInactive(prev => !prev);
  };

  // Create new plan
  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!newPlan.name || !newPlan.type || (newPlan.type === 'quota' && !newPlan.quota)) {
      setError('Please fill in all required fields.');
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/plans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: selectedProduct.id,
          name: newPlan.name,
          type: newPlan.type,
          quota: newPlan.type === 'quota' ? parseInt(newPlan.quota, 10) : undefined,
          deliveryMode: newPlan.deliveryMode,
          active: newPlan.active // Include active status in creation request
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to create plan');
      setSuccess('Plan created successfully!');
      setNewPlan({ name: '', type: 'consumption', quota: '', deliveryMode: 'cloud', active: true });
      fetchPlans();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update an existing plan
  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    if (!editingPlan) return;

    setError(null);
    setSuccess(null);

    // Validate
    if (!editForm.name) {
      setError('Plan name is required.');
      return;
    }

    if (editForm.type === 'quota' && !editForm.quota) {
      setError('Quota is required for quota-based plans.');
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/plans/${editingPlan.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editForm.name,
          type: editForm.type,
          quota: editForm.type === 'quota' ? parseInt(editForm.quota, 10) : undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to update plan');
      
      setSuccess('Plan updated successfully!');
      setEditingPlan(null);
      setEditForm({ name: '', type: '', quota: '' });
      fetchPlans();
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle plan active status
  const handleToggleActive = async (planId, currentStatus) => {
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/plans/${planId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active: !currentStatus }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to update plan status');
      
      // Use the actual status from the backend response instead of assuming it changed
      const newStatus = typeof data.active === 'boolean' ? data.active : !currentStatus;
      console.log(`Plan ${planId} status updated in backend to: ${newStatus}`);
      
      // Update the local state with the confirmed status from backend
      setPlans(prevPlans => prevPlans.map(plan => 
        plan.id === planId ? { ...plan, active: newStatus } : plan
      ));
      
      setSuccess(`Plan ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      console.error('Error toggling plan status:', err);
      setError(err.message);
    }
  };

  return (
    <div className="bg-gray-800 text-gray-200 p-6 rounded-lg shadow-lg">
      {error && <div className="text-red-400 mb-4 p-2 border border-red-700 bg-red-900 bg-opacity-30 rounded">{error}</div>}
      {success && <div className="text-green-400 mb-4 p-2 border border-green-700 bg-green-900 bg-opacity-30 rounded">{success}</div>}
      
      {!selectedProduct ? (
        <div>Please select a product to manage its plans.</div>
      ) : (
        <>
          {/* List existing plans */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h3 className="text-lg font-medium text-gray-100">Existing Plans</h3>
              {selectedProduct && (
                <div className="mt-1 text-xs text-gray-300 flex items-center gap-2">
                  <span className="font-mono">Product ID: {selectedProduct.id}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedProduct.id)}
                    className="p-1 rounded hover:bg-white/10 text-gray-300 hover:text-white transition"
                    title="Copy product ID"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              )}
            </div>
            <label className="flex items-center text-sm">
              <input 
                type="checkbox" 
                checked={showInactive} 
                onChange={handleToggleShowInactive}
                className="mr-2 h-4 w-4 rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              Show Inactive Plans
            </label>
          </div>
          {loading ? (
            <div className="text-blue-300 animate-pulse">Loading plans...</div>
          ) : plans.length === 0 ? (
            <div className="text-gray-400 italic mb-6">No plans found for this product. Create your first plan below.</div>
          ) : (
            <div className="overflow-x-auto mb-8">
              <table className="min-w-full text-sm mb-4 border-collapse">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Name</th>
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Type</th>
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Delivery</th>
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Quota</th>
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Status</th>
                    <th className="px-4 py-2 text-left border-b border-gray-600 text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans
                    // Filter based on showInactive setting
                    .filter(plan => showInactive || plan.active)
                    .map(plan => (
                      <tr key={plan.id} className={`border-b border-gray-700 ${!plan.active ? 'text-gray-500' : ''}`}>
                        {/* If currently editing this plan, show edit form row */}
                        {editingPlan && editingPlan.id === plan.id ? (
                          <td colSpan="6" className="px-4 py-3">
                            <form onSubmit={handleUpdatePlan} className="space-y-3">
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label htmlFor="edit-name" className="block text-xs font-medium mb-1 text-gray-300">Plan Name</label>
                                  <input
                                    id="edit-name"
                                    type="text"
                                    name="name"
                                    value={editForm.name}
                                    onChange={handleEditInputChange}
                                    className="w-full px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    required
                                  />
                                </div>
                                <div>
                                  <label htmlFor="edit-type" className="block text-xs font-medium mb-1 text-gray-300">Type</label>
                                  <select
                                    id="edit-type"
                                    name="type"
                                    value={editForm.type}
                                    onChange={handleEditInputChange}
                                    className="w-full px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    required
                                  >
                                    <option value="consumption">Consumption</option>
                                    <option value="quota">Quota</option>
                                  </select>
                                </div>
                                {editForm.type === 'quota' && (
                                  <div>
                                    <label htmlFor="edit-quota" className="block text-xs font-medium mb-1 text-gray-300">Quota</label>
                                    <input
                                      id="edit-quota"
                                      type="number"
                                      name="quota"
                                      value={editForm.quota}
                                      onChange={handleEditInputChange}
                                      className="w-full px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                      min="1"
                                      required
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-end space-x-2">
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 text-sm bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  className="px-3 py-1 text-sm bg-blue-700 text-white rounded hover:bg-blue-600"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </form>
                          </td>
                        ) : (
                          // Regular display row
                          <>
                            <td className="px-4 py-3">
                              <div>{plan.name}</div>
                              <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                                <span className="font-mono break-all">Plan ID: {plan.id}</span>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(plan.id)}
                                  className="p-1 rounded hover:bg-white/10 text-gray-300 hover:text-white transition"
                                  title="Copy plan ID"
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 capitalize">{plan.type}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${plan.delivery_mode === 'offline' ? 'bg-purple-900 text-purple-100' : 'bg-blue-900 text-blue-100'}`}>
                                {plan.delivery_mode === 'offline' ? 'Offline' : 'Cloud'}
                              </span>
                            </td>
                            <td className="px-4 py-3">{plan.type === 'quota' ? plan.quota.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${plan.active ? 'bg-green-900 text-green-100' : 'bg-red-900 text-red-100'}`}>
                                {plan.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 flex space-x-2">
                              <button
                                className={`px-3 py-1 text-xs rounded-md ${plan.active 
                                  ? 'bg-yellow-700 hover:bg-yellow-600 text-yellow-100' 
                                  : 'bg-green-700 hover:bg-green-600 text-green-100'}`}
                                onClick={() => handleToggleActive(plan.id, plan.active)}
                                title={plan.active ? 'Set plan to inactive' : 'Set plan to active'}
                              >
                                {plan.active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                className="px-3 py-1 text-xs bg-blue-700 text-blue-100 rounded-md hover:bg-blue-600"
                                onClick={() => handleStartEdit(plan)}
                              >
                                Edit
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Form to create a new plan */}
          <div className="bg-gray-700 p-5 rounded-lg">
            <h3 className="text-lg font-medium mb-4 text-white">Create New Plan</h3>
            <form onSubmit={handleCreatePlan} className="space-y-4">
              <div>
                <label htmlFor="plan-name" className="block text-sm font-medium mb-1 text-gray-300">
                  Plan Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="plan-name"
                  type="text"
                  name="name"
                  value={newPlan.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Basic Consumption, 10K Quota, Enterprise"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-400">Give your plan a descriptive name</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="plan-type" className="block text-sm font-medium mb-1 text-gray-300">
                    Plan Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="plan-type"
                    name="type"
                    value={newPlan.type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="consumption">Consumption (pay-as-you-go)</option>
                    <option value="quota">Quota (fixed limit)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    {newPlan.type === 'consumption'
                      ? 'Consumption plans allow unlimited usage billed based on actual usage.'
                      : 'Quota plans allow usage up to a specified limit.'}
                  </p>
                </div>

                <div>
                  <label htmlFor="plan-delivery-mode" className="block text-sm font-medium mb-1 text-gray-300">
                    Delivery Mode <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="plan-delivery-mode"
                    name="deliveryMode"
                    value={newPlan.deliveryMode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="cloud">Cloud (API access)</option>
                    <option value="offline">Offline (License Manager)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    {newPlan.deliveryMode === 'cloud'
                      ? 'Cloud plans use API keys validated against the cloud service.'
                      : 'Offline plans are delivered via the License Manager for air-gapped deployments.'}
                  </p>
                </div>
              </div>
              
              {newPlan.type === 'quota' && (
                <div>
                  <label htmlFor="plan-quota" className="block text-sm font-medium mb-1 text-gray-300">
                    Quota Limit <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="plan-quota"
                    type="number"
                    name="quota"
                    value={newPlan.quota}
                    onChange={handleInputChange}
                    placeholder="Enter maximum units allowed (e.g., 10000)"
                    min="1"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required={newPlan.type === 'quota'}
                  />
                  <p className="mt-1 text-xs text-gray-400">Specify the maximum number of units allowed for this plan</p>
                </div>
              )}
              
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  Create Plan
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
